import { Effect } from 'effect'
import { sort } from '../sort'
import type { FileReadError } from './archive-errors'
import type { FileEntry } from './file-entry'

export { type FileEntry } from './file-entry'

export type UnpackOpts = {
  shouldIgnore: (filename: string) => boolean
}

export type UploadDirectory = {
  readonly name: string
  readonly readEntries: (
    shouldIgnore: UnpackOpts['shouldIgnore']
  ) => Effect.Effect<FileEntry[], FileReadError>
}

/** Opaque upload capabilities captured while the browser drop event is active. */
export type UploadSelection = {
  readonly items: ReadonlyArray<
    | { readonly type: 'file'; readonly entry: FileEntry }
    | { readonly type: 'directory'; readonly directory: UploadDirectory }
  >
}

export function unpackUpload(
  upload: UploadSelection,
  opts: Partial<UnpackOpts> = {}
): Effect.Effect<FileEntry[], FileReadError> {
  const shouldIgnore = opts.shouldIgnore ?? defaultIgnore

  return Effect.gen(function* () {
    const allEntries: FileEntry[] = []
    for (const item of upload.items) {
      if (item.type == 'file') {
        if (!shouldIgnore(item.entry.path)) {
          allEntries.push(item.entry)
        }
        continue
      }

      if (shouldIgnore(item.directory.name)) continue
      allEntries.push(...(yield* item.directory.readEntries(shouldIgnore)))
    }

    sort(allEntries, (a, b) => a.path.localeCompare(b.path))
    return allEntries
  })
}

function defaultIgnore(name: string) {
  return name == '.git' || name == '.DS_Store'
}
